// @flow

import * as React from 'react';
import {useEffect} from 'react';
import {every} from 'lodash';
import ReactFlow, {
  Background,
  ReactFlowProvider,
  useStoreState,
  useZoomPanHelper,
  Controls,
} from 'react-flow-renderer';
import Box from 'sail/Box';
import {OnlyRenderOnClient} from 'docs/components';
import {
  SequenceDiagramActionNode,
  SequenceDiagramActorNode,
} from './SequenceDiagram';
import {StateDiagramNode} from './StateDiagram/StateDiagramNode';
import {OverviewDiagramIconNode} from './OverviewDiagram/OverviewDiagramIconNode';
import {OverviewDiagramTextNode} from './OverviewDiagram/OverviewDiagramTextNode';
import {EntityDiagramNode} from './EntityDiagram/EntityDiagramNode';
import {positionElements, positionSequenceElements} from './util';

import type {DiagramType} from './types';
import './Diagram.scss';

const nodeTypes = {
  sequenceActor: SequenceDiagramActorNode,
  sequenceAction: SequenceDiagramActionNode,
  state: StateDiagramNode,
  icon: OverviewDiagramIconNode,
  text: OverviewDiagramTextNode,
  entity: EntityDiagramNode,
};

const onLoad = (reactFlowInstance) => {
  reactFlowInstance.fitView();
};

type DiagramContentProps = {|
  initialElements: Array<any>,
  type: DiagramType,
|};

const DiagramContent = ({initialElements, type}: DiagramContentProps) => {
  // Internal state provided by react-flow-renderer to let us know the dimensions of the rendered nodes
  const nodes = useStoreState((store) => store.nodes);
  const {fitView} = useZoomPanHelper();

  const [buildGraph, setBuildGraph] = React.useState(true);
  const [fitGraph, setFitGraph] = React.useState(false);
  const [elements, setElements] = React.useState(initialElements);

  const hasDimensions = (n) => {
    return n && n.__rf.width !== null && n.__rf.height !== null;
  };

  useEffect(() => {
    // only build the graph if the nodes have been rendered on the page
    // so we know their height and width before calculating their position
    if (buildGraph && nodes?.length && every(nodes, hasDimensions)) {
      const positionedElements =
        type === 'sequence'
          ? positionSequenceElements(nodes, initialElements)
          : positionElements(nodes, initialElements);

      setBuildGraph(false);
      setElements(positionedElements);
      setFitGraph(true);
    }
  }, [nodes, initialElements, buildGraph, type]);

  useEffect(() => {
    if (fitGraph) {
      // Once we position the elements correct, we can center the view
      fitView({padding: 0.3});
      setFitGraph(false);
    }
  }, [fitGraph, fitView]);

  return (
    <Box className="Diagram" margin={{top: 12}}>
      <ReactFlow
        elements={elements}
        nodeTypes={nodeTypes}
        onLoad={onLoad}
        nodesDraggable={false}
        nodesConnectable={false}
        zoomOnScroll={false}
        zoomOnPinch={false}
      >
        <Background variant="dots" gap={16} size={1} color="#C0C8D2" />
        <Controls />
      </ReactFlow>
    </Box>
  );
};

type DiagramProps = {|
  elements: Array<any>,
  type: DiagramType,
|};
export const Diagram = ({elements, type}: DiagramProps) => {
  return (
    <OnlyRenderOnClient>
      <ReactFlowProvider>
        <DiagramContent initialElements={elements} type={type} />
      </ReactFlowProvider>
    </OnlyRenderOnClient>
  );
};
